/**
 * 写真メモ機能ユーティリティ
 * - EXIF GPS座標抽出
 * - VPSアップロード
 * - Google Vision OCR
 * - DB保存
 * - ローカルドロー連携
 */

import exifr from 'exifr'
import ExifReader from 'exifreader'
import { appendIdToken } from '@/js/utils/firebaseAuth'

// API エンドポイント
const VPS_UPLOAD_URL = 'https://kenzkenz.net/myphp/photoMemoUpload.php'
const DB_INSERT_URL = '/oh3/php/photoMemoInsert.php'
const DB_SELECT_URL = '/oh3/php/photoMemoSelect.php'
const DB_UPDATE_URL = '/oh3/php/photoMemoUpdate.php'
const DB_DELETE_URL = '/oh3/php/photoMemoDelete.php'
const PHP_API_BASE = process.env.VUE_APP_PHP_API_BASE || '/src/php'
const GOOGLE_VISION_OCR_URL = `${PHP_API_BASE}/googleVisionOcr.php`
const WHISPER_OCR_URL = `${PHP_API_BASE}/whisperVoiceOcr.php`

export interface GpsCoords {
  lng: number
  lat: number
}

export interface PhotoMemoData {
  id?: number
  uid: string
  nickname?: string
  original_filename: string
  file_path: string
  url: string
  thumbnail_url?: string | null
  ocr_text?: string
  lng?: number | null
  lat?: number | null
  has_coords: number
  draw_feature_id?: string | null
  memo?: string
  note?: string           // 自由メモ（複数行テキスト）
  voice_text?: string      // 音声メモ文字起こし（Whisper API）
  sketch_data?: string | null // fabric.js JSON文字列（写真スケッチ）
  shot_at?: string | null // EXIF撮影日時
  created_at?: string
  updated_at?: string
}

export interface UploadResult {
  success: boolean
  file_path?: string
  url?: string
  thumbnail_url?: string | null
  original_filename?: string
  ocr_text?: string
  lng?: number | null
  lat?: number | null
  has_coords?: number
  error?: string
}

/**
 * 画像ファイルからEXIF GPS座標を抽出
 * iOS Safari対応: FileをArrayBufferに変換してから読み取る
 * フォールバック: exifr → ExifReader
 */
export async function extractGpsFromImage(file: File | Blob): Promise<GpsCoords | null> {
  // iOS Safari workaround: FileをArrayBufferに変換
  let arrayBuffer: ArrayBuffer
  try {
    arrayBuffer = await file.arrayBuffer()
  } catch {
    return null
  }

  // 方法1: exifrで試行
  try {
    const gps = await exifr.gps(arrayBuffer)
    if (gps && typeof gps.latitude === 'number' && typeof gps.longitude === 'number') {
      return {
        lat: gps.latitude,
        lng: gps.longitude,
      }
    }
  } catch {
    // exifr失敗時はExifReaderでフォールバック
  }

  // 方法2: ExifReaderでフォールバック
  try {
    const tags = await ExifReader.load(arrayBuffer, { expanded: true })
    if (tags.gps && tags.gps.Latitude !== undefined && tags.gps.Longitude !== undefined) {
      return {
        lat: tags.gps.Latitude,
        lng: tags.gps.Longitude,
      }
    }
  } catch {
    // ExifReaderも失敗
  }

  return null
}

/**
 * 画像ファイルから全EXIF情報を抽出
 * iOS Safari対応: FileをArrayBufferに変換してから読み取る
 */
export async function extractExifFromImage(file: File | Blob): Promise<Record<string, unknown> | null> {
  // iOS Safari workaround: FileをArrayBufferに変換
  let arrayBuffer: ArrayBuffer
  try {
    arrayBuffer = await file.arrayBuffer()
  } catch {
    console.warn('[photoMemo] ArrayBuffer変換失敗')
    return null
  }

  try {
    const exif = await exifr.parse(arrayBuffer, true)
    return exif
  } catch (error) {
    console.warn('[photoMemo] EXIF抽出失敗:', error)
    return null
  }
}

/**
 * Google Vision OCRを実行
 * 手書き文字の認識精度が高い
 */
export async function performGoogleVisionOcr(file: File | Blob): Promise<{ success: boolean; text?: string; error?: string }> {
  try {
    // ファイルをBase64に変換
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(file)
    })

    // Google Vision OCR APIを呼び出し（FormData方式 - プリフライト不要でCORS回避）
    const formData = new FormData()
    formData.append('image', base64)
    const response = await fetch(GOOGLE_VISION_OCR_URL, {
      method: 'POST',
      body: formData,
    })

    const result = await response.json()

    if (result.success && result.text) {
      return { success: true, text: result.text }
    } else if (result.error) {
      console.warn('[photoMemo] Google Vision OCR error:', result.error)
      return { success: false, error: result.error }
    }

    return { success: true, text: '' }
  } catch (error) {
    console.error('[photoMemo] Google Vision OCR failed:', error)
    return { success: false, error: 'OCR処理に失敗しました' }
  }
}

/**
 * OpenAI Whisper APIで音声を文字起こし
 * 音声メモの認識に使用
 */
export async function performWhisperOcr(audioBlob: Blob): Promise<{ success: boolean; text?: string; error?: string }> {
  try {
    // BlobをBase64に変換
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(audioBlob)
    })

    // Whisper OCR APIを呼び出し（FormData方式）
    const formData = new FormData()
    formData.append('audio', base64)
    const response = await fetch(WHISPER_OCR_URL, {
      method: 'POST',
      body: formData,
    })

    const result = await response.json()

    if (result.success && result.text) {
      return { success: true, text: result.text }
    } else if (result.error) {
      console.warn('[photoMemo] Whisper OCR error:', result.error)
      return { success: false, error: result.error }
    }

    return { success: true, text: '' }
  } catch (error) {
    console.error('[photoMemo] Whisper OCR failed:', error)
    return { success: false, error: '音声の文字起こしに失敗しました' }
  }
}

/**
 * 写真をVPSにアップロード（画像保存のみ、OCRは別途実行）
 */
export async function uploadPhotoToVps(
  file: File | Blob,
  uid: string,
  coords?: GpsCoords | null
): Promise<UploadResult> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('uid', uid)
  await appendIdToken(formData)

  if (coords) {
    formData.append('lng', coords.lng.toString())
    formData.append('lat', coords.lat.toString())
  }

  try {
    const response = await fetch(VPS_UPLOAD_URL, {
      method: 'POST',
      body: formData,
    })

    const result = await response.json()

    if (result.error) {
      return { success: false, error: result.error }
    }

    return {
      success: true,
      file_path: result.file_path,
      url: result.url,
      thumbnail_url: result.thumbnail_url,
      original_filename: result.original_filename,
      ocr_text: result.ocr_text,
      lng: result.lng,
      lat: result.lat,
      has_coords: result.has_coords,
    }
  } catch (error) {
    console.error('[photoMemo] VPSアップロード失敗:', error)
    return { success: false, error: 'アップロードに失敗しました' }
  }
}

/**
 * 写真メモデータをDBに保存
 */
export async function savePhotoMemoToDb(data: Omit<PhotoMemoData, 'id' | 'created_at' | 'updated_at'>): Promise<{ success: boolean; id?: number; error?: string }> {
  const formData = new FormData()
  formData.append('uid', data.uid)
  formData.append('nickname', data.nickname || '')
  formData.append('original_filename', data.original_filename)
  formData.append('file_path', data.file_path)
  formData.append('url', data.url)
  formData.append('thumbnail_url', data.thumbnail_url || '')
  formData.append('ocr_text', data.ocr_text || '')
  formData.append('lng', data.lng !== null && data.lng !== undefined ? data.lng.toString() : '')
  formData.append('lat', data.lat !== null && data.lat !== undefined ? data.lat.toString() : '')
  formData.append('has_coords', data.has_coords.toString())
  formData.append('draw_feature_id', data.draw_feature_id || '')
  formData.append('memo', data.memo || '')
  formData.append('note', data.note || '')
  formData.append('voice_text', data.voice_text || '')
  if (data.shot_at) {
    formData.append('shot_at', data.shot_at)
  } else {
  }

  try {
    const response = await fetch(DB_INSERT_URL, {
      method: 'POST',
      body: formData,
    })

    const result = await response.json()

    if (result.error) {
      return { success: false, error: result.error }
    }

    return { success: true, id: result.id }
  } catch (error) {
    console.error('[photoMemo] DB保存失敗:', error)
    return { success: false, error: 'DB保存に失敗しました' }
  }
}

/**
 * ユーザーの写真メモ一覧を取得
 */
export async function fetchPhotoMemos(
  uid: string,
  options?: { hasCoords?: number; limit?: number; offset?: number; isAll?: boolean }
): Promise<{ success: boolean; data?: PhotoMemoData[]; total?: number; error?: string }> {
  const params = new URLSearchParams({ uid })

  if (options?.isAll) {
    params.append('isAll', 'true')
  }
  if (options?.hasCoords !== undefined) {
    params.append('has_coords', options.hasCoords.toString())
  }
  if (options?.limit !== undefined) {
    params.append('limit', options.limit.toString())
  }
  if (options?.offset !== undefined) {
    params.append('offset', options.offset.toString())
  }

  try {
    const response = await fetch(`${DB_SELECT_URL}?${params.toString()}`)
    const result = await response.json()

    if (result.error) {
      return { success: false, error: result.error }
    }

    return {
      success: true,
      data: result.data,
      total: result.total,
    }
  } catch (error) {
    console.error('[photoMemo] データ取得失敗:', error)
    return { success: false, error: 'データ取得に失敗しました' }
  }
}

/**
 * 写真メモを削除
 */
export async function deletePhotoMemo(
  id: number,
  uid: string
): Promise<{ success: boolean; error?: string }> {
  const formData = new FormData()
  formData.append('id', id.toString())
  formData.append('uid', uid)

  try {
    const response = await fetch(DB_DELETE_URL, {
      method: 'POST',
      body: formData,
    })

    const result = await response.json()

    if (result.error) {
      return { success: false, error: result.error }
    }

    return { success: true }
  } catch (error) {
    console.error('[photoMemo] 削除失敗:', error)
    return { success: false, error: '削除に失敗しました' }
  }
}

/**
 * 写真メモを更新
 */
export async function updatePhotoMemo(
  id: number,
  uid: string,
  updates: {
    ocr_text?: string
    memo?: string
    note?: string
    lng?: number | null
    lat?: number | null
    has_coords?: number
    url?: string
    thumbnail_url?: string | null
    file_path?: string
    tags?: string
    sketch_data?: string | null
    voice_text?: string
  }
): Promise<{ success: boolean; error?: string }> {
  const formData = new FormData()
  formData.append('id', id.toString())
  formData.append('uid', uid)

  if (updates.ocr_text !== undefined) {
    formData.append('ocr_text', updates.ocr_text)
  }
  if (updates.memo !== undefined) {
    formData.append('memo', updates.memo)
  }
  if (updates.note !== undefined) {
    formData.append('note', updates.note)
  }
  if (updates.lng !== undefined) {
    formData.append('lng', updates.lng === null ? '' : updates.lng.toString())
  }
  if (updates.lat !== undefined) {
    formData.append('lat', updates.lat === null ? '' : updates.lat.toString())
  }
  if (updates.has_coords !== undefined) {
    formData.append('has_coords', updates.has_coords.toString())
  }
  if (updates.url !== undefined) {
    formData.append('url', updates.url)
  }
  if (updates.thumbnail_url !== undefined) {
    formData.append('thumbnail_url', updates.thumbnail_url || '')
  }
  if (updates.file_path !== undefined) {
    formData.append('file_path', updates.file_path)
  }
  if (updates.tags !== undefined) {
    formData.append('tags', updates.tags)
  }
  if (updates.sketch_data !== undefined) {
    formData.append('sketch_data', updates.sketch_data || '')
  }
  if (updates.voice_text !== undefined) {
    formData.append('voice_text', updates.voice_text)
  }

  try {
    const response = await fetch(DB_UPDATE_URL, {
      method: 'POST',
      body: formData,
    })

    const result = await response.json()

    if (result.error) {
      return { success: false, error: result.error }
    }

    return { success: true }
  } catch (error) {
    console.error('[photoMemo] 更新失敗:', error)
    return { success: false, error: '更新に失敗しました' }
  }
}

/**
 * テキストメモ（写真なし）をDBに保存
 */
export async function saveTextMemoToDb(data: {
  uid: string
  nickname?: string
  memo?: string
  note?: string
  ocr_text?: string
  voice_text?: string
  tags?: string
  lng?: number | null
  lat?: number | null
  has_coords?: number
}): Promise<{ success: boolean; id?: number; error?: string }> {
  const formData = new FormData()
  formData.append('uid', data.uid)
  formData.append('nickname', data.nickname || '')
  formData.append('original_filename', 'テキストメモ')
  formData.append('file_path', '__text_memo__')
  formData.append('url', '')
  formData.append('memo', data.memo || '')
  formData.append('note', data.note || '')
  formData.append('ocr_text', data.ocr_text || '')
  formData.append('voice_text', data.voice_text || '')
  formData.append('has_coords', (data.has_coords || 0).toString())
  formData.append('lng', data.lng != null ? data.lng.toString() : '')
  formData.append('lat', data.lat != null ? data.lat.toString() : '')
  if (data.tags) formData.append('tags', data.tags)

  try {
    const response = await fetch(DB_INSERT_URL, {
      method: 'POST',
      body: formData,
    })

    const result = await response.json()

    if (result.error) {
      return { success: false, error: result.error }
    }

    return { success: true, id: result.id }
  } catch (error) {
    console.error('[photoMemo] テキストメモ保存失敗:', error)
    return { success: false, error: 'テキストメモの保存に失敗しました' }
  }
}

/**
 * 写真メモをアップロードしてDBに保存（一括処理）
 * @returns 座標付きの場合はローカルドロー用のfeature IDも返す
 */
export async function processPhotoMemo(
  file: File | Blob,
  uid: string,
  nickname?: string,
  memo?: string,
  originalFilename?: string,
  overrideCoords?: GpsCoords | null,
  fallbackCoords?: GpsCoords | null,
  shotAt?: string | null, // EXIF撮影日時（圧縮前のファイルから抽出したもの）
  note?: string // 自由メモ
): Promise<{
  success: boolean
  photoMemo?: PhotoMemoData
  error?: string
  ocrError?: string
}> {
  // 1. GPS座標の決定（優先順位: overrideCoords > EXIF GPS > fallbackCoords）
  let coords = overrideCoords || null
  let coordsSource = overrideCoords ? 'override' : null

  if (!coords) {
    coords = await extractGpsFromImage(file)
    if (coords) {
      coordsSource = 'exif'
    }
  }

  if (!coords && fallbackCoords) {
    coords = fallbackCoords
    coordsSource = 'fallback'
  }

  // 2. VPSにアップロード（画像保存）
  const uploadResult = await uploadPhotoToVps(file, uid, coords)

  if (!uploadResult.success) {
    return { success: false, error: uploadResult.error }
  }

  // 3. Google Vision OCRを実行（手書き文字認識精度が高い）
  let ocrText = ''
  let ocrError = ''
  const ocrResult = await performGoogleVisionOcr(file)
  if (ocrResult.success && ocrResult.text) {
    ocrText = ocrResult.text
  } else if (!ocrResult.success && ocrResult.error) {
    ocrError = ocrResult.error
    console.warn('[photoMemo] OCRエラー（アップロードは続行）:', ocrError)
  }

  // ファイル名を取得（File の場合は name プロパティ、Blob の場合は引数から）
  const fileName = (file as File).name || originalFilename || 'image.jpg'

  // 4. DBに保存
  // 座標は、渡されたcoordsを優先し、なければVPSレスポンスを使用
  const finalLng = coords?.lng ?? uploadResult.lng ?? null
  const finalLat = coords?.lat ?? uploadResult.lat ?? null
  const finalHasCoords = (finalLng !== null && finalLat !== null) ? 1 : 0


  const photoMemoData: Omit<PhotoMemoData, 'id' | 'created_at' | 'updated_at'> = {
    uid,
    nickname: nickname || '',
    original_filename: uploadResult.original_filename || fileName,
    file_path: uploadResult.file_path || '',
    url: uploadResult.url || '',
    thumbnail_url: uploadResult.thumbnail_url,
    ocr_text: ocrText,
    lng: finalLng,
    lat: finalLat,
    has_coords: finalHasCoords,
    draw_feature_id: null, // 後でローカルドロー連携時に設定
    memo: memo || '',
    note: note || '',
    shot_at: shotAt || null, // EXIF撮影日時
  }

  const saveResult = await savePhotoMemoToDb(photoMemoData)

  if (!saveResult.success) {
    return { success: false, error: saveResult.error }
  }

  return {
    success: true,
    photoMemo: {
      ...photoMemoData,
      id: saveResult.id,
    },
    ocrError: ocrError || undefined,
  }
}

/**
 * 写真メモをローカルドローのポイントに変換
 * @param photoMemo - 写真メモデータ
 * @param options - オプション（rotationなど）
 */
export function createDrawFeatureFromPhotoMemo(
  photoMemo: PhotoMemoData,
  options?: { rotation?: number }
): GeoJSON.Feature<GeoJSON.Point> | null {
  if (!photoMemo.lng || !photoMemo.lat) {
    return null
  }

  const feature: GeoJSON.Feature<GeoJSON.Point> = {
    type: 'Feature',
    id: `photomemo-${photoMemo.id}`,
    geometry: {
      type: 'Point',
      coordinates: [photoMemo.lng, photoMemo.lat],
    },
    properties: {
      name: photoMemo.original_filename,
      label: photoMemo.memo || '',           // 名称 → 地図ラベル
      longText: photoMemo.note || '',        // 自由メモ → 長文テキスト（ドロー表示用）
      ocrText: photoMemo.ocr_text || '',     // OCR結果（ドロワーで編集可能）
      voiceText: photoMemo.voice_text || '', // 音声メモ文字起こし（ドロワーで編集可能）
      pictureUrl: photoMemo.url,             // ドローシステム用（pictureUrl）
      thumbnailUrl: photoMemo.thumbnail_url,
      photoMemoId: photoMemo.id,
      sketchData: photoMemo.sketch_data || null, // fabric.js JSON（写真スケッチ）
      created_at: photoMemo.created_at,
      rotation: options?.rotation ?? 0,      // 写真の回転角度（0, 90, 180, 270）
      // ローカルドローのスタイル用（緑丸）
      'marker-color': '#4CAF50',
      'icon-color': '#4CAF50',
      'point-color': '#4CAF50',
      'marker-size': 'medium',
      'marker-symbol': 'camera',
    },
  }

  return feature
}
